/**
 * Offscreen canvas rendering for export (copy, download, blob).
 * Deduplicates the rendering logic previously repeated in three imperative methods.
 */
import { drawTransition, drawSelfLoop } from './drawingHelpers';

/**
 * Compute the world-space bounding box of all states and transitions.
 * @param {Array} states
 * @param {Array} transitions
 * @param {CanvasRenderingContext2D} ctx - needed for text measurement
 * @returns {{ minX, minY, maxX, maxY, width, height }} or null if empty
 */
export function computeBoundingBox(states, transitions, ctx) {
  if (!states.length) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const expand = (x, y) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };

  // State circles + initial arrows
  states.forEach(s => {
    const extra = s.is_initial ? 40 : 0;
    expand(s.x - s.radius - extra, s.y - s.radius);
    expand(s.x + s.radius, s.y + s.radius);
  });

  // Transitions (curves, self-loops, labels)
  transitions.forEach(t => {
    const fromState = states.find(s => s.id === t.from_state);
    const toState = states.find(s => s.id === t.to_state);
    if (!fromState || !toState) return;

    if (fromState.id === toState.id) {
      // Self-loop ellipse
      const angle = t.loopAngle !== undefined ? t.loopAngle : -Math.PI / 2;
      const loopWidth = 20, loopHeight = 30;
      const d = fromState.radius + loopHeight - 5;
      const cx = fromState.x + Math.cos(angle) * d;
      const cy = fromState.y + Math.sin(angle) * d;
      const rotation = angle + Math.PI / 2;

      // Sample points around the rotated ellipse
      for (let i = 0; i < 8; i++) {
        const p = (i / 8) * Math.PI * 2;
        const lx = loopWidth * Math.cos(p), ly = loopHeight * Math.sin(p);
        expand(
          cx + lx * Math.cos(rotation) - ly * Math.sin(rotation),
          cy + lx * Math.sin(rotation) + ly * Math.cos(rotation)
        );
      }

      // Self-loop label
      const labelLines = t.pda_rules?.length > 0
        ? t.pda_rules.map(r => `${r.input || '\u03BB'}, ${r.pop || '\u03BB'} \u2192 ${r.push || '\u03BB'}`)
        : (t.symbols?.length > 0 ? [t.symbols.join(', ')] : []);
      if (labelLines.length > 0 && labelLines[0] && ctx) {
        const labelGap = 10;
        const nx = Math.sin(rotation), ny = -Math.cos(rotation);
        ctx.font = '12px JetBrains Mono';
        const lineHeight = 14;
        const maxTextW = Math.max(...labelLines.map(l => ctx.measureText(l).width));
        const totalH = labelLines.length * lineHeight;
        const halfProj = Math.abs(nx) * maxTextW / 2 + Math.abs(ny) * totalH / 2;
        const baseDist = loopHeight + labelGap;
        const lx = cx + nx * (baseDist + halfProj);
        const ly = cy + ny * (baseDist + halfProj);
        expand(lx - maxTextW / 2, ly - totalH / 2);
        expand(lx + maxTextW / 2, ly + totalH / 2);
      }
    } else {
      // Regular transition — include control point and label
      const dx = toState.x - fromState.x, dy = toState.y - fromState.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist === 0) return;
      const perpX = -dy / dist, perpY = dx / dist;

      let curve = t.curve || 0;
      if (curve === 0) {
        const hasReverse = transitions.some(r => r.from_state === t.to_state && r.to_state === t.from_state);
        if (hasReverse) curve = 1.2;
      }
      const curveOffset = curve * dist * 0.5;
      const midX = (fromState.x + toState.x) / 2, midY = (fromState.y + toState.y) / 2;
      const ctrlX = midX + perpX * curveOffset, ctrlY = midY + perpY * curveOffset;
      expand(ctrlX, ctrlY);

      // Label bounding box
      const labelLines = t.pda_rules?.length > 0
        ? t.pda_rules.map(r => `${r.input || '\u03BB'}, ${r.pop || '\u03BB'} \u2192 ${r.push || '\u03BB'}`)
        : (t.symbols?.length > 0 ? [t.symbols.join(', ')] : []);
      if (labelLines.length > 0 && labelLines[0] && ctx) {
        const fDx = ctrlX - fromState.x, fDy = ctrlY - fromState.y;
        const fDist = Math.sqrt(fDx * fDx + fDy * fDy);
        const startX = fromState.x + (fDx / fDist) * fromState.radius;
        const startY = fromState.y + (fDy / fDist) * fromState.radius;
        const tDx = toState.x - ctrlX, tDy = toState.y - ctrlY;
        const tDist = Math.sqrt(tDx * tDx + tDy * tDy);
        const endX = toState.x - (tDx / tDist) * toState.radius;
        const endY = toState.y - (tDy / tDist) * toState.radius;
        const arcMidX = (startX + 2 * ctrlX + endX) / 4;
        const arcMidY = (startY + 2 * ctrlY + endY) / 4;
        const sign = curveOffset >= 0 ? 1 : -1;
        const nx = perpX * sign, ny = perpY * sign;
        ctx.font = '12px JetBrains Mono';
        const lineHeight = 14;
        const maxTextW = Math.max(...labelLines.map(l => ctx.measureText(l).width));
        const totalH = labelLines.length * lineHeight;
        const halfProj = Math.abs(nx) * maxTextW / 2 + Math.abs(ny) * totalH / 2;
        const lx = arcMidX + nx * (10 + halfProj);
        const ly = arcMidY + ny * (10 + halfProj);
        expand(lx - maxTextW / 2, ly - totalH / 2);
        expand(lx + maxTextW / 2, ly + totalH / 2);
      }
    }
  });

  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;

  return { minX, minY, maxX, maxY, width, height };
}

/** Draw all transitions onto a context (for offscreen export). */
function drawTransitionsForExport(ctx, states, transitions, colors) {
  transitions.forEach(transition => {
    const from = states.find(s => s.id === transition.from_state);
    const to = states.find(s => s.id === transition.to_state);
    if (!from || !to) return;
    ctx.strokeStyle = colors.transitionStroke;
    ctx.lineWidth = 2;
    if (from.id === to.id) {
      drawSelfLoop(ctx, from, transition, colors, transition.loopAngle, false);
    } else {
      let curve = transition.curve || 0;
      if (curve === 0 && transitions.some(r => r.from_state === transition.to_state && r.to_state === transition.from_state)) {
        curve = 1.2;
      }
      drawTransition(ctx, from, to, transition, colors, curve, false);
    }
  });
}

/** Draw all states onto a context (for offscreen export). */
function drawStatesForExport(ctx, states, colors) {
  states.forEach(state => {
    // Initial arrow
    if (state.is_initial) {
      ctx.strokeStyle = colors.stateStroke;
      ctx.fillStyle = colors.stateStroke;
      ctx.lineWidth = 2;
      const arrowLen = 40;
      const arrowX = state.x - state.radius - arrowLen;
      const tipX = state.x - state.radius;
      const tipY = state.y;
      ctx.beginPath();
      ctx.moveTo(arrowX, state.y);
      ctx.lineTo(tipX, tipY);
      ctx.stroke();
      const ahl = 10;
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(tipX - ahl * Math.cos(-Math.PI / 6), tipY - ahl * Math.sin(-Math.PI / 6));
      ctx.lineTo(tipX - ahl * Math.cos(Math.PI / 6), tipY - ahl * Math.sin(Math.PI / 6));
      ctx.closePath();
      ctx.fill();
    }

    // Circle
    ctx.beginPath();
    ctx.arc(state.x, state.y, state.radius, 0, Math.PI * 2);
    ctx.fillStyle = colors.stateFill;
    ctx.fill();
    ctx.strokeStyle = state.is_final ? colors.stateFinalStroke : colors.stateStroke;
    ctx.lineWidth = 3;
    ctx.stroke();

    // Final inner circle
    if (state.is_final) {
      ctx.beginPath();
      ctx.arc(state.x, state.y, state.radius - 6, 0, Math.PI * 2);
      ctx.strokeStyle = colors.stateFinalStroke;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Name
    ctx.fillStyle = colors.textPrimary;
    ctx.font = '14px JetBrains Mono';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(state.name, state.x, state.y);
  });
}

/**
 * Render the full automaton to an offscreen 2x canvas.
 * Returns the canvas element, or null if the automaton is empty.
 */
export function renderToOffscreenCanvas(states, transitions, colors, padding = 60) {
  if (!states.length) return null;

  // Temp canvas for text measurement
  const tmpCanvas = document.createElement('canvas');
  const tmpCtx = tmpCanvas.getContext('2d');
  tmpCtx.font = '12px JetBrains Mono';

  const bbox = computeBoundingBox(states, transitions, tmpCtx);
  if (!bbox) return null;

  const imgW = bbox.width + padding * 2;
  const imgH = bbox.height + padding * 2;
  const scale = 2;

  const offCanvas = document.createElement('canvas');
  offCanvas.width = imgW * scale;
  offCanvas.height = imgH * scale;
  const ctx = offCanvas.getContext('2d');
  ctx.scale(scale, scale);

  // Background
  ctx.fillStyle = colors.background;
  ctx.fillRect(0, 0, imgW, imgH);

  // Translate so world (minX, minY) maps to (padding, padding)
  ctx.save();
  ctx.translate(padding - bbox.minX, padding - bbox.minY);

  drawTransitionsForExport(ctx, states, transitions, colors);
  drawStatesForExport(ctx, states, colors);

  ctx.restore();
  return offCanvas;
}
